import { cast, flow, getSnapshot, SnapshotIn, toGenerator, types } from 'mobx-state-tree'

import { FetchModel } from '../models/FetchModel'
import {
  DiffKind,
  ExistingFlightNetworkNodeModel,
  FlightNetwork,
  FlightNetworkEdge,
  FlightNetworkEdgeModel,
  FlightNetworkModel,
  FlightNetworkNode,
  FlightNetworkNodeKind,
  FlightNetworkNodeModel,
} from '../models/FlightNetwork/FlightNetworkModel'
import { FilterModel } from '../models/common/Filters/FilterModel'
import { FilterType } from '../models/common/Filters/FilterType'
import { SelectFieldFilter, SelectFilterIcon } from '../models/common/Filters/SelectFieldFilter'
import {
  OperationFailure,
  OperationInProgress,
  OperationNotStarted,
  OperationStatusModel,
  OperationSuccess,
} from '../models/common/OperationStatus'

import type { SelectOptions } from '../components/Select/Select'
import type { SearchFieldFilter } from '../models/common/Filters/SearchFieldFilterModel'
import { Key } from 'ts-key-enum'
import { Launchpad, LaunchpadModel } from '../models/FlightNetwork/LaunchpadModel'
import { DraftEditor, DraftMetadata, DraftNetworkInfo, DraftVersionModel } from '../models/FlightNetwork/FlightNetworkDraftModel'

const flightNetworkNodeKindSelectOptions: SelectOptions = Object.values(FlightNetworkNodeKind).map((nodeKind) => ({
  value: nodeKind,
  text: nodeKind.charAt(0).toUpperCase() + nodeKind.slice(1),
}))

const diffKindSelectOptions: SelectOptions = Object.values(DiffKind).map((diffKind) => ({
  value: diffKind.toString(),
  text: diffKind.toString().charAt(0).toUpperCase() + diffKind.toString().slice(1),
}))

const NODE_KIND_FILTER = 'flightNetworkNodeKind'
const SEARCH_FILTER_NAME = 'search'
const DIFF_KIND_FILTER_NAME = 'diffKind'

const FLIGHT_NETWORK_FILTERS: SnapshotIn<typeof FilterModel> = {
  fieldFilters: {
    [SEARCH_FILTER_NAME]: {
      type: FilterType.SEARCH,
      placeholder: 'Search',
    },
    [NODE_KIND_FILTER]: {
      type: FilterType.SELECT,
      icon: SelectFilterIcon.FlightNetworkNodeKind,
      options: flightNetworkNodeKindSelectOptions.slice(), // copy to make non readonly
    },
    [DIFF_KIND_FILTER_NAME]: {
      type: FilterType.SELECT,
      icon: SelectFilterIcon.Filter,
      options: diffKindSelectOptions.slice()
    },
  },
}

export enum MapEditingMode {
  Drag = 'Drag',
  Ruler = 'Ruler',
  Polygon = 'Polygon',
  ConnectEdges = 'ConnectEdges',
  AddDeliveryPoint = 'AddDeliveryPoint',
  AddSemaphore = 'AddSemaphore',
  AddWaypoint = 'AddWaypoint',
  AddLaunchpad = 'AddLaunchpad',
}

type StartEditingDraftOptions = 
{
  allowTakeover: true,
  onDraftEditedByOtherUser?: never
} | {
  allowTakeover: false,
  onDraftEditedByOtherUser: (draftEditor: DraftEditor) => void
}

export const FlightNetworkStore = FetchModel.named('FlightNetworkStore')
  .props({
    isEditingDraft: types.maybeNull(types.boolean),

    currentFlightNetwork: types.maybeNull(FlightNetworkModel),

    launchpads: types.maybeNull(types.array(LaunchpadModel)),

    draftVersion: types.maybeNull(DraftVersionModel), 
    draftFlightNetwork: types.maybeNull(FlightNetworkModel),

    fetchFlightNetworkStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),

    enterDraftEditingModeStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    exitDraftEditingModeStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    saveDraftStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    publishDraftStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    discardDraftStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    
    draftPreSaveValidationStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    validateFlightNetworkStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),

    // last clicked node when creating edges between nodes using the "connect" tool in the map.
    lastConnectedNode: types.safeReference(FlightNetworkNodeModel),

    filters: types.optional(FilterModel, FLIGHT_NETWORK_FILTERS),

    mapEditingMode: types.maybeNull(types.enumeration<MapEditingMode>('MapEditingMode', Object.values(MapEditingMode))),
    
    selectedNode: types.safeReference(FlightNetworkNodeModel),
    selectedEdge: types.safeReference(FlightNetworkEdgeModel),
  })
  .views((self) => ({
    get getCurrentFlightNetworkEndpointUrl(): string {
      return `${self.baseUrl}flightnetworks/get_current/`
    },
    get draftMetadataEndpointUrl(): string {
      return `${self.baseUrl}flightnetworks/draft/get_draft_metadata/`
    },
    get draftNetworkEndpointUrl(): string {
      return `${self.baseUrl}flightnetworks/draft/get_draft/`
    },
    get launchpadsEndpointUrl(): string {
      return `${self.baseUrl}launchpads/`
    },
    get saveDraftUrl(): string {
      return `${self.baseUrl}flightnetworks/draft/save_draft/`
    },
    get startEditingDraftUrl(): string {
      return `${self.baseUrl}flightnetworks/draft/start_editing_draft/`
    },
    get finishEditingDraftUrl(): string {
      return `${self.baseUrl}flightnetworks/draft/finish_editing_draft/`
    },
    get publishDraftUrl(): string {
      return `${self.baseUrl}flightnetworks/draft/publish_draft/`
    },
    get discardDraftUrl(): string {
      return `${self.baseUrl}flightnetworks/draft/discard_draft/`
    },
    get searchFilter() {
      return self.filters.fieldFilters.get(SEARCH_FILTER_NAME) as SearchFieldFilter
    },
    get nodeKindFilter() {
      return self.filters.fieldFilters.get(NODE_KIND_FILTER) as SelectFieldFilter
    },
    get diffKindFilter() {
      return self.filters.fieldFilters.get(DIFF_KIND_FILTER_NAME) as SelectFieldFilter
    },
  }))
  .views((self) => ({
    // Currently displayed flight network - operational of draft
    get flightNetwork(): FlightNetwork | null {
      return self.isEditingDraft ? self.draftFlightNetwork : self.currentFlightNetwork
    }
  }))
  .views((self) => ({
    get filteredNodes(): Array<FlightNetworkNode> | null {
      if (self.flightNetwork === null) {
        return null
      }
      const searchFilterValue = self.searchFilter.value?.toLocaleLowerCase()
      const kindFilterValue = self.nodeKindFilter?.filterValue?.value
      const diffKindFilterValue = self.diffKindFilter?.filterValue?.value
      return self.flightNetwork.nodes.filter(
        (node) => {
          return (searchFilterValue === undefined || 
            node.name.toLocaleLowerCase().includes(searchFilterValue) || 
            (ExistingFlightNetworkNodeModel.is(node) && node.uuid.toLocaleLowerCase().includes(searchFilterValue))) &&
          (kindFilterValue === undefined || node.nodeKind === kindFilterValue) && 
          (diffKindFilterValue === undefined || node.diffFromOperationalNetwork === diffKindFilterValue)
        }
      )
    },
  }))
  .actions((self) => ({
    _loadDraftMetadata: flow(function* _loadDraftMetadata() {
      return yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<DraftMetadata>>(
          self.draftMetadataEndpointUrl,
          { method: 'POST' },
          { fetchOperationName: 'Fetch flight network draft metadata' },
        )
      )
    }),
    _loadDraftNetwork: flow(function* _loadDraftNetwork() {
      return yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<DraftNetworkInfo>>(
          self.draftNetworkEndpointUrl,
          { method: 'POST' },
          { fetchOperationName: 'Fetch flight network draft' },
        )
      )
    }),
    _loadCurrentFlightNetwork: flow(function* _fetchFlightNetwork() {
      return yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<FlightNetwork>>(
          self.getCurrentFlightNetworkEndpointUrl,
          { method: 'POST' }, 
          { fetchOperationName: 'Fetch flight network' },
        ),
      )
    }),
    _loadLaunchpads: flow(function* _fetchLaunchpads() {
      return yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<Launchpad[]>>(
          self.launchpadsEndpointUrl,
          {  }, 
          { fetchOperationName: 'Fetch launchpads' },
        ),
      )
    })
  }))
  .actions((self) => ({
    fetchAndSetFlightNetwork: flow(function* fetchAndSetFlightNetwork() {
      self.fetchFlightNetworkStatus = OperationInProgress()

      // Fetch current flight network
      // Even if user is seeing the draft, we need the operational network for diff calculations (to show the colors)
      const loadFlightNetworkResult = yield* toGenerator(self._loadCurrentFlightNetwork())
      if (loadFlightNetworkResult.error) {
        self.fetchFlightNetworkStatus = OperationFailure(loadFlightNetworkResult.error)
        return
      } 
      // Fetch launchpads
      const loadLaunchpadsResult = yield* toGenerator(self._loadLaunchpads())
      if (loadLaunchpadsResult.error) {
        self.fetchFlightNetworkStatus = OperationFailure(loadLaunchpadsResult.error)
        return
      } 

      // Fetch draft metadata, if user is editor - fetch the the draft. 
      // Otherwise don't bother to fetch draft, fetch only operational network.      
      const loadDraftMetadataResult = yield* toGenerator(self._loadDraftMetadata())
      if (loadDraftMetadataResult.error) {
        self.fetchFlightNetworkStatus = OperationFailure(loadDraftMetadataResult.error)
        return
      }

      if (loadDraftMetadataResult.result.is_current_user_editor) {
        // Fetch draft network
        const loadFlightNetworkDraftResult = yield* toGenerator(self._loadDraftNetwork())
        if (loadFlightNetworkDraftResult.error) {
          self.fetchFlightNetworkStatus = OperationFailure(loadFlightNetworkDraftResult.error)
          return
        } 
        if (!loadFlightNetworkDraftResult.result.draft) {
          self.fetchFlightNetworkStatus = OperationFailure({
            name: 'Logical Error', 
            message: 'You are marked as draft editor but there seems not to be a draft. Try to refresh the page'})
          return
        }
        self.isEditingDraft = true
        self.draftVersion = DraftVersionModel.create(loadFlightNetworkDraftResult.result.draft.draft_version)
        self.draftFlightNetwork = FlightNetworkModel.create(loadFlightNetworkDraftResult.result.draft.flight_network)
      } else {
        self.isEditingDraft = false
        self.draftFlightNetwork = null
        self.draftVersion = null
      }

      self.launchpads = cast(loadLaunchpadsResult.result)
      self.currentFlightNetwork = FlightNetworkModel.create(loadFlightNetworkResult.result)
      if (self.draftFlightNetwork !== null) {
        self.draftFlightNetwork.calculateDiffsFromOperationalNetwork(self.currentFlightNetwork)
      }
      self.fetchFlightNetworkStatus = OperationSuccess()
    })
  }))
  .actions((self) => ({
    _validateDraftReadyToSave(): void {
      if (self.draftFlightNetwork === null) {
        throw Error("Can't validate flight network if no draft network loaded")
      }
      self.draftPreSaveValidationStatus = OperationNotStarted()

      const allNodesValid = self.draftFlightNetwork.nodes.every(node => node.isValid)

      self.draftPreSaveValidationStatus = allNodesValid ? 
        OperationSuccess() : 
        OperationFailure({ name: 'ValidationError', message: 'Some nodes are missing required fields'})
    },
    _saveDraftFlightNetwork: flow(function* _saveDraftFlightNetwork() {
      if (self.draftFlightNetwork === null) {
        throw Error("Can't save draft flight network if no draft network loaded")
      }
      if (self.draftVersion === null) {
        throw Error("Can't save draft flight network, draft version unknown")
      }
      self.saveDraftStatus = OperationInProgress()

      const flightNetworkSnapshot = {
        ...getSnapshot(self.draftFlightNetwork),
        nodes: self.draftFlightNetwork.nodes
          .filter(node => node.diffFromOperationalNetwork !== DiffKind.DELETED)
          .map(node => getSnapshot(node)),
        edges: self.draftFlightNetwork.edges
          .filter(edge => edge.diffFromOperationalNetwork !== DiffKind.DELETED)
          .map(edge => getSnapshot(edge)),
    }
      const draftInfo = {
        draft_version: self.draftVersion,
        flight_network: flightNetworkSnapshot,
      }
      const saveDraftResult = yield* toGenerator(
        self.loadObjectOrError<void>(
          self.saveDraftUrl,
          { method: 'POST', body: JSON.stringify(draftInfo) },
          { fetchOperationName: 'Save draft flight network' },
        ),
      )

      if (saveDraftResult.error) {
        self.saveDraftStatus = OperationFailure(saveDraftResult.error)
      } else {
        self.saveDraftStatus = OperationSuccess()
        void self.fetchAndSetFlightNetwork()
      }
    }),
  }))
  .actions((self) => ({
    enterDraftEditingMode: flow(function* enterDraftEditingMode({ allowTakeover, onDraftEditedByOtherUser }: StartEditingDraftOptions) {
      self.enterDraftEditingModeStatus = OperationInProgress()

      const loadDraftMetadataResult = yield* toGenerator(self._loadDraftMetadata())
      if (loadDraftMetadataResult.error) {
        self.enterDraftEditingModeStatus = OperationFailure(loadDraftMetadataResult.error)
        return
      }
      const draftMetadata = loadDraftMetadataResult.result

      if (draftMetadata.draft_editor && !draftMetadata.is_current_user_editor && !allowTakeover) {
        onDraftEditedByOtherUser(draftMetadata.draft_editor)
        return
      }
      const startEditingRequest = {
        'draft_version': draftMetadata.draft_version,
        'allowed_to_takeover': allowTakeover
      }
      const startEditingDraftResult = yield* toGenerator(
        self.loadObjectOrError<void>(
          self.startEditingDraftUrl,
          { method: 'POST', body: JSON.stringify(startEditingRequest) },
          { fetchOperationName: 'Start editing flight network draft' },
        ),
      )

      if (startEditingDraftResult.error) {
        self.enterDraftEditingModeStatus = OperationFailure(startEditingDraftResult.error)
        return
      }
      
      self.enterDraftEditingModeStatus = OperationSuccess()
      // self.isEditingDraft will be set to true by fetchAndSetFlightNetwork 
      // once it loads draft metadata and sees that we are marked as draft editor on the backend.
      void self.fetchAndSetFlightNetwork()

    }),
    exitDraftEditingMode: flow(function* exitDraftEditingMode() {
      self.exitDraftEditingModeStatus = OperationInProgress()
      

      const finishEditingDraftResult = yield* toGenerator(
        self.loadObjectOrError<void>(
          self.finishEditingDraftUrl,
          { method: 'POST' },
          { fetchOperationName: 'Finish editing flight network draft' },
        ),
      )

      if (finishEditingDraftResult.error) {
        self.exitDraftEditingModeStatus = OperationFailure(finishEditingDraftResult.error)
        return
      }
      
      self.exitDraftEditingModeStatus = OperationSuccess()
      // self.isEditingDraft will be set to false by fetchAndSetFlightNetwork 
      // once it loads draft metadata and sees that we are no longer marked as draft editor on the backend.
      void self.fetchAndSetFlightNetwork()

    }),
    saveDraft: flow(function* saveDraft() {
      if (!self.isEditingDraft) {
        self.saveDraftStatus = OperationFailure({name: 'StateError', message: "Can't save draft when you're not in editing mode"})
        return
      }
      self._validateDraftReadyToSave()
      if (self.draftPreSaveValidationStatus.error) {
        return
      }
      yield* toGenerator(self._saveDraftFlightNetwork())
    }),
    publishDraft: flow(function* publishDraft() {
      if (!self.isEditingDraft) {
        throw Error("Can't publish draft network if you're not in editing mode")
      }
      if (self.draftVersion === null) {
        throw Error("Can't publish draft flight network - draft version not loaded")
      }
      self.publishDraftStatus = OperationInProgress()

      const publishDraftResult = yield* toGenerator(
        self.loadObjectOrError<void>(
          self.publishDraftUrl,
          { method: 'POST', body: JSON.stringify({ draft_version: getSnapshot(self.draftVersion) }) },
          { fetchOperationName: 'Publish draft flight network' },
        ),
      )

      if (publishDraftResult.error) {
        self.publishDraftStatus = OperationFailure(publishDraftResult.error)
      } else {
        self.publishDraftStatus = OperationSuccess()
        void self.fetchAndSetFlightNetwork()
      }    
    }),
    discardDraft: flow(function* discardDraft() {
      if (!self.isEditingDraft) {
        throw Error("Can't discard draft network if you're not in editing mode")
      }
      if (self.draftVersion === null) {
        throw Error("Can't publish draft flight network - draft version not loaded")
      }
      self.discardDraftStatus = OperationInProgress()

      const discardDraftResult = yield* toGenerator(
        self.loadObjectOrError<void>(
          self.discardDraftUrl,
          { method: 'POST', body: JSON.stringify({ draft_version: getSnapshot(self.draftVersion) }) },
          { fetchOperationName: 'Discard draft flight network' },
        ),
      )

      if (discardDraftResult.error) {
        self.discardDraftStatus = OperationFailure(discardDraftResult.error)
      } else {
        self.discardDraftStatus = OperationSuccess()
        void self.fetchAndSetFlightNetwork()
      }
    }),
    setMapEditingMode(mode: typeof self.mapEditingMode) {
      if (mode !== null) {
        self.selectedNode = undefined
        self.selectedEdge = undefined
      }
      if (self.mapEditingMode === MapEditingMode.ConnectEdges && 
          mode !== MapEditingMode.ConnectEdges) {
            self.lastConnectedNode = undefined
      }
      self.mapEditingMode = mode
    },
    toggleSelectedNode(node: typeof self.selectedNode) {
      if (node === self.selectedNode) {
        self.selectedNode = undefined
      } else {
        self.selectedNode = node
      }
      self.selectedEdge = undefined
    },
    toggleSelectedEdge(edge: typeof self.selectedEdge) {
      if (edge === self.selectedEdge) {
        self.selectedEdge = undefined
      } else {
        self.selectedEdge = edge
      }
      self.selectedNode = undefined
    },
    setLastConnectedNode(node: typeof self.lastConnectedNode) {
      self.lastConnectedNode = node
    },
  }))
  .actions((self) => ({
    addNetworkNode(kind: FlightNetworkNodeKind, lat: number, lon: number) {
      if (self.flightNetwork === null) {
        throw Error("Can't add network node if network not fetched") 
      }
      const newNode = self.flightNetwork.addNode(kind, lat, lon)
      self.toggleSelectedNode(newNode)
    },
    deleteNetworkNode(node: FlightNetworkNode) {
      if (self.flightNetwork === null) {
        throw Error("Can't add network node if network not fetched") 
      }
      if (self.selectedNode === node) {
        self.selectedNode = undefined
      }
      self.flightNetwork.deleteNode(node)
    },
  }))
  .actions((self) => ({
    handleNodeMapClick (clickedNode: FlightNetworkNode) {
      if (self.flightNetwork === null) {
        return
      }
      switch (self.mapEditingMode) {
        case MapEditingMode.ConnectEdges:
          if (self.lastConnectedNode !== undefined) {
            self.flightNetwork.addEdge(self.lastConnectedNode, clickedNode)
          }
          self.setLastConnectedNode(clickedNode)
          break;
        default:
          self.toggleSelectedNode(clickedNode)
          break;
      }
    },
    handleEdgeMapClick (clickedEdge: FlightNetworkEdge) {
      if (self.flightNetwork === null) {
        return
      }
      self.toggleSelectedEdge(clickedEdge)
    },
    handleKeyPress (e: KeyboardEvent) {
      if (self.isEditingDraft && e.key === Key.Backspace) {
        if (self.flightNetwork !== null && self.selectedEdge !== undefined) {
          self.flightNetwork.deleteEdge(self.selectedEdge)
          self.setLastConnectedNode(undefined)
        } else if (self.flightNetwork !== null && self.selectedNode !== undefined) {
          self.flightNetwork.deleteNode(self.selectedNode)
          self.setLastConnectedNode(undefined)
        }
      }
    }
  }))
